// 10 — Broody. A hen that has decided to sit, and the three things a keeper can
// do about it (REFOCUS §12b).
//
// Whether she is broody and whether her eggs are fertile are the sim's. The
// costs and the two paths that do not exist yet — a fertile-egg shipment and a
// broody breaker — are PLAN-04's, and their buttons say so in a dev build
// instead of pretending to work.

import type { ChickenEntity } from '../../core/types';
import { t } from '../../i18n';
import { chip } from '../components/chip';
import { button, div, h, link, paint } from '../dom';
import { placeholderBroodyChoices, placeholderBroodyCosts, type BroodyChoice } from '../placeholders';
import { portraitSprite } from '../selectors';
import type { Screen, ScreenContext } from '../router';

export function broodyScreen(): Screen<HTMLElement> {
  return {
    id: 'broody',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const { store, shell } = ctx.services;
      const state = store.getState();
      const hen = state.flock.find((bird) => bird.alive && bird.currentState === 'BROODY');
      if (!hen) return noBroodyHen(ctx);

      const choices = placeholderBroodyChoices();
      // Without a rooster her eggs cannot hatch, which is the whole question
      // this screen answers (REFOCUS §12b).
      const hasRooster = state.flock.some((bird) => bird.alive && bird.gender === 'rooster');
      let picked: BroodyChoice = choices[0];

      const costHost = div({});
      const choiceHost = div({ class: 'cf-stack cf-stack--tight' });
      const foot = div({ class: 'cf-screen__foot' });

      const paintCosts = (): void => {
        paint(
          costHost,
          picked.id,
          () =>
            div(
              { class: 'cf-cost' },
              h('span', { class: 'cf-label', text: t('broody.whatItCosts') }),
              ...placeholderBroodyCosts(picked.id === 'incubator').map((key) =>
                div(
                  {
                    class: `cf-cost__item${key.endsWith('1') ? '' : ' cf-cost__item--bad'}`,
                  },
                  h('span', { class: 'cf-cost__dot', attrs: { 'aria-hidden': 'true' } }),
                  h('span', { text: t(key, { name: hen.name }) }),
                ),
              ),
            ),
        );
      };

      const paintChoices = (): void => {
        paint(
          choiceHost,
          picked.id,
          () =>
            div(
              { class: 'cf-stack cf-stack--tight' },
              ...choices.map((choice) =>
                button(
                  {
                    class: 'cf-choice cf-choice--wide',
                    attrs: { 'aria-pressed': String(choice.id === picked.id) },
                    on: {
                      click: () => {
                        picked = choice;
                        paintChoices();
                        paintCosts();
                        paintFoot();
                      },
                    },
                  },
                  h(
                    'div',
                    { class: 'cf-choicerow' },
                    h('span', { class: 'cf-choice__title', text: t(choice.labelKey) }),
                    h('span', {
                      class: 'cf-choice__sub',
                      text: choice.cost === 0 ? t('common.free') : `${choice.cost} ${t('common.eggits')}`,
                    }),
                  ),
                  h('span', { class: 'cf-choice__sub', text: t(choice.noteKey) }),
                ),
              ),
            ),
        );
      };

      const paintFoot = (): void => {
        paint(foot, picked.id, () =>
          div(
            { class: 'cf-screen__foot' },
            button({
              class: 'cf-btn cf-btn--primary',
              text: t(picked.actionKey),
              on: {
                click: () => {
                  // PLAN-04 owns the fertile-egg shipment, the incubator and the
                  // broody breaker. Until then the tap says which system is
                  // missing rather than silently doing nothing.
                  shell.toast(t('broody.landsInU6', { system: t(picked.actionKey) }));
                },
              },
            }),
          ),
        );
      };

      paintChoices();
      paintCosts();
      paintFoot();

      const screen = div(
        { class: 'cf-screen' },
        div(
          { class: 'cf-screen__head' },
          link({
            class: 'cf-btn cf-btn--link',
            text: t('coop.backToCoop'),
            on: { click: () => ctx.router.back() },
          }),
        ),
        broodyHeader(hen, hasRooster),
        h('p', { class: 'cf-sub', text: t(hasRooster ? 'broody.ledBy' : 'broody.noRooster') }),
        choiceHost,
        costHost,
        foot,
      );
      screen.tabIndex = -1;
      return screen;
    },
  };
}

function broodyHeader(hen: ChickenEntity, hasRooster: boolean): HTMLElement {
  return div(
    { class: 'cf-warninghead' },
    h('img', {
      class: 'cf-warninghead__img',
      attrs: { src: portraitSprite(hen), alt: t('bird.portraitAlt', { name: hen.name }) },
    }),
    div(
      { class: 'cf-stack cf-stack--tight' },
      h('h1', { class: 'cf-display', text: t('broody.title', { name: hen.name }) }),
      h('span', { class: 'cf-sub', text: t('broody.subtitle') }),
      hasRooster
        ? chip({ text: t('broody.roosterPresent'), tone: 'good' })
        : chip({ text: t('broody.noRoosterChip'), tone: 'warn' }),
    ),
  );
}

function noBroodyHen(ctx: ScreenContext<HTMLElement>): HTMLElement {
  const screen = div(
    { class: 'cf-screen' },
    h('h1', { class: 'cf-display', text: t('broody.noneTitle') }),
    h('p', { class: 'cf-sub', text: t('broody.noneNote') }),
    div(
      { class: 'cf-screen__foot' },
      button({
        class: 'cf-btn cf-btn--primary',
        text: t('common.back'),
        on: { click: () => ctx.router.back() },
      }),
    ),
  );
  screen.tabIndex = -1;
  return screen;
}
